/**
 * The offer status machine, on the client, and the words for the offer vocabulary —
 * statuses, the Phase 1 actions, and their labels.
 *
 * The offer analogue of `web/src/recruiting/interview.ts`, treated the same way: the
 * set of things a caller may do to an offer is **derived from a table**, so an illegal
 * action has no button rather than a disabled one, and the server's `409
 * WORKFORCE_INVALID_STATE_TRANSITION` — carrying `details.allowed_transitions[]` — is
 * what actually decides. This copy exists so the detail can offer the legal moves
 * *before* a refusal; the refusal's list is authoritative and is printed verbatim when
 * the two ever disagree.
 *
 * ── The machine (transcribed from `openapi.yaml` `RecruitingOfferStatus`) ────
 *
 *   draft            → pending_approval | approved
 *   pending_approval → approved
 *   approved         → sent | rescinded
 *   sent             → accepted | declined | rescinded | expired
 *   accepted / declined / rescinded / expired — terminal, no move out
 *
 * ── Transitions the machine carries vs. actions this build can drive ──────────
 *
 * The status machine is transcribed whole, terminal rows included, because "is this
 * terminal" must be a fact read off the table rather than an omission. But the Phase 1
 * API drives only three verbs — **approve**, **send**, **respond** — so the *buttons*
 * come from `OFFER_STATUS_ACTIONS`, a second table mapping a status to the actions that
 * have an endpoint. `draft → pending_approval`, `approved → rescinded` and
 * `sent → {rescinded, expired}` are legal transitions with no Phase 1 write, so they
 * carry no control here; the server owns them.
 *
 * Transcribed rather than imported, for the reason `interview.ts` gives: the SPA bundle
 * is served to browsers, and a `web/ → src/modules/` import would drag a `pg` handle
 * into one.
 */

import type { MessageKey } from '../i18n/messages';
import type { Severity } from '../states/icons';
import type { OfferStatus } from '../api/recruiting';

export { OFFER_STATUSES, OFFER_RESPONSE_OUTCOMES } from '../api/recruiting';
export type { OfferStatus, OfferResponseOutcome } from '../api/recruiting';

/**
 * Every status has a row, the terminal four included, whose row is the empty list. An
 * absent key and an empty list are different statements; keeping the empty rows makes
 * "is this terminal" a fact read off the table rather than an omission.
 */
export const OFFER_TRANSITIONS: Readonly<Record<OfferStatus, readonly OfferStatus[]>> = {
  draft: ['pending_approval', 'approved'],
  pending_approval: ['approved'],
  approved: ['sent', 'rescinded'],
  sent: ['accepted', 'declined', 'rescinded', 'expired'],
  accepted: [],
  declined: [],
  rescinded: [],
  expired: [],
};

/** `true` for exactly the statuses with no outgoing edge. Derived, never listed. */
export function isTerminalOfferStatus(status: OfferStatus): boolean {
  return OFFER_TRANSITIONS[status].length === 0;
}

/**
 * The Phase 1 actions a caller can take on an offer, each backed by an endpoint. A
 * `respond` is split into its two outcomes so each is its own control — the allowed
 * set the screen renders is exactly these, and nothing that lacks an endpoint.
 */
export const OFFER_ACTIONS = ['approve', 'send', 'respond_accepted', 'respond_declined'] as const;

export type OfferAction = (typeof OFFER_ACTIONS)[number];

/**
 * A status → the actions this build can drive from it. Every non-terminal status has at
 * least one action (there is no status that is live in the machine yet has no Phase 1
 * write), so a status with an empty action list is exactly a terminal one.
 */
export const OFFER_STATUS_ACTIONS: Readonly<Record<OfferStatus, readonly OfferAction[]>> = {
  draft: ['approve'],
  pending_approval: ['approve'],
  approved: ['send'],
  sent: ['respond_accepted', 'respond_declined'],
  accepted: [],
  declined: [],
  rescinded: [],
  expired: [],
};

export function offerActions(from: OfferStatus): readonly OfferAction[] {
  return OFFER_STATUS_ACTIONS[from];
}

/** The name of a status, for the badge and the detail. */
export const OFFER_STATUS_LABEL: Readonly<Record<OfferStatus, MessageKey>> = {
  draft: 'recruiting.offers.status.draft',
  pending_approval: 'recruiting.offers.status.pending_approval',
  approved: 'recruiting.offers.status.approved',
  sent: 'recruiting.offers.status.sent',
  accepted: 'recruiting.offers.status.accepted',
  declined: 'recruiting.offers.status.declined',
  rescinded: 'recruiting.offers.status.rescinded',
  expired: 'recruiting.offers.status.expired',
};

/** What pressing an action control **does**, as a verb — not the name of a status. */
export const OFFER_ACTION_LABEL: Readonly<Record<OfferAction, MessageKey>> = {
  approve: 'recruiting.offers.action.approve',
  send: 'recruiting.offers.action.send',
  respond_accepted: 'recruiting.offers.action.respond_accepted',
  respond_declined: 'recruiting.offers.action.respond_declined',
};

/**
 * The badge severity a status is drawn at. An in-flight offer (draft, pending, approved,
 * sent) reads `info`; an `accepted` offer `low` (the good terminal); a `declined`,
 * `rescinded` or `expired` offer `medium` (a terminal to notice). Icon and text carry
 * it, as everywhere; colour is the third channel (DESIGN.md §4).
 */
export const OFFER_STATUS_BADGE: Readonly<Record<OfferStatus, Severity>> = {
  draft: 'info',
  pending_approval: 'info',
  approved: 'info',
  sent: 'info',
  accepted: 'low',
  declined: 'medium',
  rescinded: 'medium',
  expired: 'medium',
};
